import { render } from "micromustache";
import { PropertyConfig } from "../../../forms/propertyConfig";
import { ValidationError } from "../../../forms/validationError";
import { anyObject, getProp } from "../../../tools/any.tools";
import { isNumeric } from "../../../tools/number.tools";
import { ValueOrFunction, anyReallyAny, getVOFValue } from "../../../tools/object.tools";
import { formatMinuteOrSecondsHHMMAndChooseOverflow, formatMinutesHHMM } from "../../../tools/time.tools";
import { format } from "../../../tools/timestamps.tools";
import { DEFAULT_DATE_FORMAT } from "../../../tools/timestamps.tools.definitions";
import { ERROR_MESSAGES, FALLBACK_ERROR_MESSAGE } from "../validation.i18n";
import { RegisterValidator, Validator } from "./validator";

/** Validator name to register */
export const COMPARE_VALIDATOR_NAME = "compare";

/** 
 * All available comparators for validation
 * enum values are used in i18n to register a message for each comparator
 */
export enum Comparator {
    /** "===" operator */
    EQUAL = "equal",

    /** "==" operator */
    EQUAL_WITHOUT_TYPE = "equal_without_type",

    /** "!==" operator */
    NOT_EQUAL = "not_equal",

    /** "!=" operator */
    NOT_EQUAL_WITHOUT_TYPE = "not_equal_without_type",

    /** ">" operator */
    GREATER = "greater",

    /** ">=" operator */
    GREATER_OR_EQUAL = "greater_or_equal",

    /** "<" operator */
    LESS = "less",

    /** "<=" operator */
    LESS_OR_EQUAL = "less_or_equal"
}

/**
 * Validator params
 */
export interface CompareValidatorParams<ValueType = anyReallyAny> {
    comparator: Comparator;
    value?: ValueOrFunction<ValueType>;
    propertyName?: ValueOrFunction<string>;

    /** 
     * true: value compare is a date (UnixTimestamp), error message will be adapted for "day"
     */
    compareDate?: boolean;

    /**
     * true: value compare is an minute, error message will be displayed for "12:00"
     */
    compareTime?: boolean;

    /** 
     * If given, value formatter will be used to display value as given format
     * default: DEFAULT_DATE_FORMAT (dd/mm/yyyy) if isCompareDate is true
     */
    valueFormatter?: (value: ValueType) => string;
}

/**
 * This validator compare property value with a value or with an another property value by using propertyName option
 * sample use in propertyConfig => 
 * validators: validatorEntry(CompareValidator, {comparator: Comparator.EQUAL, value: 10}) or
 * validators: validatorEntry(CompareValidator, {comparator: Comparator.GREATER, propertyName: "startDate"}) or
 * or validators: { compare: {comparator: Comparator.EQUAL, value: 10} } (second option requires this class to be loaded)
 */
@RegisterValidator(COMPARE_VALIDATOR_NAME)
export class CompareValidator implements Validator<anyReallyAny, CompareValidatorParams> {
    /**
     * * Use anyReallyAny instead of unknown because cannot compare two unknown values with < and > operators
     * @returns true if property value is not null or undefined
     */
    public isValid(property: PropertyConfig, propertyValue: anyReallyAny, validatorParams: CompareValidatorParams, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, anyReallyAny>): boolean {
        // Get value to compare from value param by default else from property name param
        let propertyValueToCompare = null;
        if (validatorParams.value) {
            propertyValueToCompare = getVOFValue(validatorParams.value);
        } else {
            // Get value of ValueOrFunction
            const propertyNameToCompare = getVOFValue(validatorParams.propertyName);
            if (propertyNameToCompare) {
                propertyValueToCompare = allPropertiesValue[propertyNameToCompare];
            }
        }

        // Always valid if both values are null or undefined (use RequiredValidator instead)
        if (propertyValue == null || propertyValueToCompare == null) {
            return true;
        }

        // Return compare result
        switch (validatorParams.comparator) {
            case Comparator.EQUAL:
                return propertyValue === propertyValueToCompare;
            case Comparator.EQUAL_WITHOUT_TYPE:
                // Disable eslint because typescript think that two properties with any type has the same type
                /* eslint-disable-next-line eqeqeq*/
                return propertyValue == propertyValueToCompare;
            case Comparator.NOT_EQUAL:
                return propertyValue !== propertyValueToCompare;
            case Comparator.NOT_EQUAL_WITHOUT_TYPE:
                // Disable eslint because typescript think that two properties with any type has the same type
                /* eslint-disable-next-line eqeqeq*/
                return propertyValue != propertyValueToCompare;
            case Comparator.GREATER:
                // Return true if not a number
                if (!isNumeric(propertyValue) || !isNumeric(propertyValueToCompare)) {
                    return true;
                }
                return Number(propertyValue) > Number(propertyValueToCompare);
            case Comparator.GREATER_OR_EQUAL:
                // Return true if not a number
                if (!isNumeric(propertyValue) || !isNumeric(propertyValueToCompare)) {
                    return true;
                }
                return Number(propertyValue) >= Number(propertyValueToCompare);
            case Comparator.LESS:
                // Return true if not a number
                if (!isNumeric(propertyValue) || !isNumeric(propertyValueToCompare)) {
                    return true;
                }
                return Number(propertyValue) < Number(propertyValueToCompare);
            case Comparator.LESS_OR_EQUAL:
                // Return true if not a number
                if (!isNumeric(propertyValue) || !isNumeric(propertyValueToCompare)) {
                    return true;
                }
                return Number(propertyValue) <= Number(propertyValueToCompare);
            default:
                return true;
        }
    }

    /**
     * @returns error message to show when not valid
     */
    public getError?(lang: string, property: PropertyConfig, propertyValue: anyReallyAny, validatorParams: CompareValidatorParams, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, anyReallyAny>): ValidationError {

        // Check if compare validator use dates
        const isCompareDate = Boolean(validatorParams.compareDate);
        const isCompareTime = Boolean(validatorParams.compareTime);
        const valueFormatter = validatorParams.valueFormatter;


        // Get error message from i18n and compile with micromustache
        const errorMessage = getProp<anyObject>(ERROR_MESSAGES, lang)[COMPARE_VALIDATOR_NAME][validatorParams.comparator + (isCompareDate ? "_date" : "") + (isCompareTime ? "_time" : "")] ?? getProp<anyObject>(ERROR_MESSAGES, lang)[validatorParams.comparator][FALLBACK_ERROR_MESSAGE] ?? "Validation i18n error";

        // Show value in error message if exists, else show property label
        let valueOrPropertyLabel = null;
        let valueOrPropertyLabelTime = null;
        const valueToCompare = getVOFValue(validatorParams.value);
        if (valueToCompare) {
            const formattedValue = valueFormatter?.(valueToCompare);
            if (formattedValue) {
                valueOrPropertyLabel = valueFormatter?.(valueToCompare);
            } else if (isCompareDate) {
                valueOrPropertyLabel = format(valueToCompare, DEFAULT_DATE_FORMAT);
            } else if (isCompareTime) {
                valueOrPropertyLabel = formatMinuteOrSecondsHHMMAndChooseOverflow(valueToCompare, false, { i18n: { dayLabel: getProp<anyObject>(ERROR_MESSAGES, lang)["labelDays"] } });
                valueOrPropertyLabelTime = valueToCompare >= 0 ? formatMinutesHHMM(valueToCompare) : "-" + formatMinutesHHMM(-1 * valueToCompare);
            } else {
                valueOrPropertyLabel = valueToCompare;
            }
        } else {
            const propertyNameToCompare = getVOFValue(validatorParams.propertyName);
            if (propertyNameToCompare) {
                valueOrPropertyLabel = allProperties[propertyNameToCompare]?.label;
            }
        }

        // Look at i18n from object keys
        const errorMessageData = {
            propLabel: property.label,
            valueOrPropertyLabel: valueOrPropertyLabel ?? "",
            valueOrPropertyLabelTime: valueOrPropertyLabelTime ?? ""
        };

        return { errorMessage: render(errorMessage, errorMessageData) };
    }
}